import React, { useState } from "react";
import Sidebar from "../Components/SideBar";
import DateRangeCompOnboarding from "../Components/DateRangeCompOnboarding";
import { useAuth } from "react-oidc-context";
import "./Onboarding.css";
import { useEffect } from "react";
import { SGD_API_HOST } from "./../env";

function Onboarding() {
  const auth = useAuth();
  const [showNewUsersOnly, setShowNewUsersOnly] = useState(false);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fromDate, setFromDate] = useState(
    new Date(new Date().setDate(new Date().getDate() - 30))
      .toISOString()
      .split("T")[0]
  );
  const [toDate, setToDate] = useState(
    new Date().toISOString().split("T")[0]
  );

  useEffect(() => {
    if (!auth.user?.access_token) return; 
    setLoading(true);
    setError(null);
  
    const url = `${SGD_API_HOST}/api/Onboarding/GetOnboardingData?startDate=${fromDate}&endDate=${toDate}`;
  
    fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${auth.user.access_token}`,
      },
    })
      .then((res) => {
        if (!res.ok) {
          //throw new Error("Failed to fetch onboarding data");
          return <div className="noDataRow">No clients found for the selected period</div>;
        }
        return res.json();
      })
      .then((data) => {
        const rawReps = data?.SalesRepresentatives || [];
  
        const normalized = rawReps.map((r) => ({
          Id: r.Id,
          Name: r.Name,
          CareOfs: r.CareOfs || [],
        }));
  
        setClients(normalized);
      })
      .catch((err) => {
        setError(err.message);
        setClients([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [fromDate, toDate, auth.user?.access_token]);  

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "$0.00";
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(value);
    } catch (e) {
      return `$${Number(value).toFixed(2)}`;
    }
  };

  const formatAmount = (value) => {
    if (value === null || value === undefined) return "0.00";
    try {
      // format number without currency symbol
      return new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    } catch (e) {
      return Number(value).toFixed(2);
    }
  };

  return (
    <div className="governanceContainer">
      <div className="staticElement">
        <Sidebar />
      </div>
      <div className="window">
        <div className="onboardingContent">
          <div className="onboardingControls">
            <div className="dateRangeWrapper">
              <DateRangeCompOnboarding
                onRangeChange={(start, end) => {
                  setFromDate(start.toISOString().split("T")[0]);
                  setToDate(end.toISOString().split("T")[0]);
                }}
              />
            </div>
            <div className="switchArea">
              <div
                className={"themeSwitch " + (showNewUsersOnly ? "on" : "")}
                role="button"
                tabIndex={0}
                onClick={() => setShowNewUsersOnly(!showNewUsersOnly)}                
                aria-pressed={showNewUsersOnly}
              >
                <div className="switchKnob" />
              </div>
              <div className="switchLabel">New users only</div>
            </div>
          </div>
  
          <div className="tableWrapper">
            {loading && <div className="loadingSpinner">Loading...</div>}
            {error && <div className="errorMessage">{error}</div>}
            {!loading && !error && (() => {
              const getVisibleCareOfs = (rep) => {
                const careList = rep.CareOfs || [];
                return showNewUsersOnly
                  ? careList.filter(c => c.IsNew || c.IsNewCustomer)                    
                  : careList;
              };
  
              const totalVisible = clients.reduce((sum, rep) => {
                return sum + getVisibleCareOfs(rep).length;
              }, 0);
  
              if (totalVisible === 0) {
                return <div className="noDataRow">No clients found for the selected period</div>;
              }
  
              return clients.map((rep) => {
                const visibleCare = getVisibleCareOfs(rep).sort((a, b) => {
                  const nameA = (a.CustomerName || "").toLowerCase();
                  const nameB = (b.CustomerName || "").toLowerCase();
                  return nameA.localeCompare(nameB);
                });
  
                if (visibleCare.length === 0) return null;
  
                return (
                  <div key={rep.Id} style={{ marginBottom: 22 }}>
                    <div className="groupHeader">{rep.Name}</div>
                    <table className="onboardingTable">
                      <thead>
                        <tr>
                          <th className="name">Name</th>
                          <th className="customer">Customer</th>
                          <th className="numeric"># Quotes</th>
                          <th className="numeric"># Takeoffs</th>
                          <th className="currencySign"></th>
                          <th className="currency">$ Quotes</th>
                          <th className="currencySign"></th>
                          <th className="currency">$ Takeoffs</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleCare.map((client) => {
                          const clientName = (
                            `${client.FirstName || ''} ${client.LastName || ''}`.trim()
                            ) || '-';                          
                            
                          const customerName = client.CustomerName || "-";
                          const quotes = client.QuoteCount ?? 0;
                          const takeoffs = client.TakeOffCount ?? 0;
                          const quotesPrice = client.TotalQuotePrice ?? 0;
                          const takeoffPrice = client.TotalTakeOffPrice ?? 0;
  
                          return (
                            <tr key={client.Id}>
                              <td className="name">{clientName}</td>
                              <td className="customer">{customerName}</td>
                              <td className="numeric">{quotes}</td>
                              <td className="numeric">{takeoffs}</td>
                              <td className="currencySign">$</td>
                              <td className="currency amount">{formatAmount(quotesPrice)}</td>
                              <td className="currencySign">$</td>
                              <td className="currency amount">{formatAmount(takeoffPrice)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Onboarding;
